import {
  Share2,
  Heart,
  Phone,
  MessageCircle,
  MapPin,
  Home,
  DollarSign,
  Ruler,
  Building,
} from "lucide-react";

const formatPrice = (price) => {
  if (!price || price === 0) return "NA";
  const numPrice = Number(price);
  
  if (numPrice >= 10000000) {
    // 1 crore or more
    const croreValue = numPrice / 10000000;
    return `₹${croreValue % 1 === 0 ? croreValue.toFixed(0) : croreValue.toFixed(2)}Cr`;
  } else if (numPrice >= 100000) {
    // 1 lac or more
    const lacValue = numPrice / 100000;
    return `₹${lacValue % 1 === 0 ? lacValue.toFixed(0) : lacValue.toFixed(2)}Lac`;
  } else {
    return `₹${numPrice}`;
  }
};

export default function RequirementCard({
  id,
  date,
  title,
  type,
  rentMin,
  rentMax,
  sizeMin,
  sizeMax,
  areas,
  condition,
  status,
  description,
  preferences,
  contactName,
  contactPhone,
  sizeRange,
}) {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 w-full">
      <div className="p-4 lg:p-5">
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-1">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900">{title}</h2>

              <div className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg">
                <Home className="w-4 h-4" />
                <span className="text-sm font-semibold">{type}</span>
              </div>
              <div className="flex gap-3  ml-auto">
                <div className="rounded-full text-red-500 font-medium px-2 py-1 border border-red-500 whitespace-nowrap text-sm">
                  {date}
                </div>
                <div className="rounded-full text-blue-600 font-medium px-2 py-1 border border-blue-600 whitespace-nowrap text-sm">
                  ID: {id}
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-1 ml-4">
            <button className="p-1.5 hover:bg-gray-100 rounded-full transition-colors">
              <Share2 className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Property Type */}
        <div className="mb-3"></div>

        {/* Requirements Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
          {/* Rent Range */}
          <div>
            <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
              <DollarSign className="w-3.5 h-3.5" />
              <span>Rent Range</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
               {formatPrice(rentMin)} - {formatPrice(rentMax)}
            </div>
          </div>



          {/* Size (BHK) */}
          <div>
            <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
              <Home className="w-3.5 h-3.5" />
              <span>Size (BHK)</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {sizeMin} - {sizeMax}
            </div>
          </div>

          {/* ✅ Added: SizeRange Display */}
          {sizeRange && (
            <div className="mt-2 space-y-3">
              {sizeRange.sqFt &&
                (sizeRange.sqFt.min > 0 || sizeRange.sqFt.max > 0) && (
                  <div>
                    <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
                      <Ruler className="w-3.5 h-3.5 text-blue-500" />
                      <span>Size (SqFt)</span>
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {sizeRange.sqFt.min} - {sizeRange.sqFt.max}
                    </div>
                  </div>
                )}

              {sizeRange.sqYd &&
                (sizeRange.sqYd.min > 0 || sizeRange.sqYd.max > 0) && (
                  <div>
                    <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-1">
                      <Ruler className="w-3.5 h-3.5 text-green-600" />
                      <span>Size (SqYd)</span>
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {sizeRange.sqYd.min} - {sizeRange.sqYd.max}
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* Condition */}
          {condition && (
            <div className="text-center">
              <div className="text-xs text-gray-500 mt-1">Status</div>
              <div className="text-lg font-semibold text-gray-900">
                {status}
              </div>
            </div>
          )}
        </div>

        {/* Preferred Areas */}
       <div className="mb-4">
  <div className="flex gap-3">
    {/* Preferred Areas Section */}
    <div className="flex-1">
      <div className="flex items-center gap-1.5 text-gray-700 text-sm font-semibold mb-2">
        <MapPin className="w-4 h-4" />
        <span>Preferred Areas ({areas.length})</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {areas.map((area, index) => (
          <span
            key={index}
            className="bg-white border border-gray-200 text-gray-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-gray-400 transition-colors"
          >
            {area}
          </span>
        ))}
      </div>
    </div>

    {/* Preferences Section */}
    <div className="flex-1">
      <div className="flex items-center gap-1.5 text-gray-700 text-sm font-semibold mb-2">
        <span>Preferences :</span>
      </div>
      <div className="flex flex-wrap gap-2">
        <span className="bg-white border border-gray-200 text-gray-700 text-xs font-medium px-3 py-1.5 rounded-full">
          {preferences}
        </span>
      </div>
    </div>
  </div>
</div>

        {/* Description */}
        <div className="mb-4 pb-4 border-b border-gray-200">
          <h3 className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
            <span className="text-orange-500">⚡</span>
            Description
          </h3>
          <p className="text-xs text-gray-600 mb-2">{description}</p>
          <div></div>
        </div>
      </div>
    </div>
  );
}
